import {
  AuditLogEndpointResponse,
  EnvironmentKey,
  HTTPMethod,
  ConfigEntityAuthAction,
} from '@web-config-app/core';
import { useQuery } from '@tanstack/react-query';
import {
  API_JSON_CONTENT_TYPE,
  buildConfigEntityAuthHeaders,
} from '@web-config-app/api';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';

export const useFetchAuditLog = ({
  environmentKey,
  instanceId,
  domainEntityType,
  enabled = false,
  pageOffset,
  pageSize,
}: {
  environmentKey: EnvironmentKey;
  instanceId: string;
  domainEntityType: string;
  enabled?: boolean;
  pageOffset?: string;
  pageSize?: string;
}) => {
  const environmentFetch = useEnvironmentFetch(environmentKey);

  const queryFn = async () => {
    const endpointConfig = {
      path: `/v1/config-audit-logs`,
      method: 'GET' as HTTPMethod,
      queryParameters: [
        {
          name: 'filter[entityID]',
          in: 'query' as const,
          required: true,
          schema: {
            type: 'string' as const,
          },
          description: 'The ID of the entity to fetch audit logs for',
        },
        {
          name: 'page[offset]',
          in: 'query' as const,
          required: false,
          schema: {
            type: 'string' as const,
          },
          description: 'Pagination offset',
        },
        {
          name: 'page[size]',
          in: 'query' as const,
          required: false,
          schema: {
            type: 'string' as const,
          },
          description: 'Pagination page size',
        },
      ],
    };

    const { endpointFetch } = getEntityEndpoint<AuditLogEndpointResponse>(
      endpointConfig,
      environmentFetch,
    );

    return endpointFetch({
      params: [
        {
          name: 'filter[entityID]',
          value: instanceId,
        },
        ...(pageOffset
          ? [
              {
                name: 'page[offset]' as const,
                value: pageOffset,
              },
            ]
          : []),
        ...(pageSize
          ? [
              {
                name: 'page[size]' as const,
                value: pageSize,
              },
            ]
          : []),
      ],
      headers: {
        'Content-Type': API_JSON_CONTENT_TYPE,
        ...buildConfigEntityAuthHeaders(
          domainEntityType,
          ConfigEntityAuthAction.Get,
        ),
      },
    });
  };

  const { data: queryResultData, ...queryResult } = useQuery({
    queryKey: [
      'audit-log',
      environmentKey,
      instanceId,
      domainEntityType,
      pageOffset,
      pageSize,
    ],
    queryFn,
    enabled,
  });

  return {
    ...queryResult,
    data: queryResultData,
  };
};
